import { KeyValue, KeyValuePipe, NgFor, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogRef,
  NbIconModule,
  NbSpinnerModule,
  NbToastrService
} from '@nebular/theme';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import JSZip from 'jszip';
import { Content } from 'src/app/models/content';
import { allLanguages } from '../../constants/translation-languages';

@Component({
  selector: 'app-preview-content',
  templateUrl: './preview-content.component.html',
  styleUrls: ['./preview-content.component.css'],
  imports: [
    NgIf,
    NgFor,
    NgSwitch,
    NgSwitchCase,
    NbSpinnerModule,
    NbIconModule,
    NbCardModule,
    NbButtonModule,
    NgSelectComponent,
    FormsModule,
    NgOptionComponent,
    KeyValuePipe
  ]
})
export class PreviewContentComponent implements OnInit {
  @Input() public content: Content;
  @Input() public values;
  @Input() public isSpecialOffer;
  public loadingMessage = '';
  public isLoading = false;
  public button;
  public buttonLink;
  public elements;
  public language = 'source';
  public translationLanguages = allLanguages;
  public isNonLocalized = true;

  constructor(
    protected ref: NbDialogRef<PreviewContentComponent>,
    public toastrService: NbToastrService
  ) {}

  public ngOnInit(): void {
    this.isNonLocalized = this.values.length < 1;
    this.button = this.getActionButton();
    this.buttonLink = this.getButtonLink();
    this.elements = this.getElements();
    if (this.elements.length === 0) {
      this.toastrService.show(status, $localize`Cannot show preview, content elements missing`, {
        status: 'danger'
      });
      this.ref.close();
    }
    this.loadImage();
  }

  public sortAlphabeticallyByValue(a: KeyValue<string, string>, b: KeyValue<string, string>): number {
    return a.value.localeCompare(b.value);
  }

  public static getDrbIds(content: Content) {
    const drbIds = [
      content.payload_records[0]?.image_id,
      content.payload_records[0]?.content_text_id,
      content.headline_text_id,
      content.payload_records[0]?.content_html_id,
      content.action_button?.length > 0 ? content.action_button[0].text_id : undefined,
      content.action_button?.length > 0 ? content.action_button[0].destination_uri_id : undefined
    ];
    return drbIds.filter(Boolean);
  }

  public languageChange(language: any) {
    this.button = this.getActionButton();
    this.elements = this.getElements();
    if (this.elements.length < 2) {
      this.elements.push({
        tag: 'warning',
        value: $localize`We cannot show the full Content Preview as some ${this.translationLanguages[language]} translations are missing.`,
        order: -1
      });
    }
    this.loadImage();
  }

  private async loadImage(): Promise<void> {
    const imageElement = this.elements.find((el: any) => el.tag === 'img');
    if (!imageElement) {
      return;
    }

    try {
      this.isLoading = true;
      this.loadingMessage = $localize`Loading image...`;
      const newImage = await this.getImageFromZip(imageElement.url);

      imageElement.value = newImage;
      this.isLoading = false;
    } catch {
      imageElement.alt = $localize`Cannot load the image`;
      this.toastrService.show(status, $localize`Image not found`, {
        status: 'danger'
      });
      this.isLoading = false;
      this.loadingMessage = $localize`Failed to load image.`;
    }
  }

  private async getImageFromZip(imageUrl: string): Promise<any> {
    let file = null;
    const lastSlash = imageUrl.lastIndexOf('/');
    const url = imageUrl.slice(0, lastSlash);
    const imageName = imageUrl.slice(lastSlash + 1);
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const arrayBuffer = await response.arrayBuffer();
    const zip = await JSZip.loadAsync(arrayBuffer);

    zip.forEach((zipPath, zipFile) => {
      const zipPathArray = zipPath.split('/');
      const parentDir = zipPathArray[zipPathArray.length - 2];
      const fileName = zipPathArray[zipPathArray.length - 1];

      if (parentDir === 'drawable-xhdpi' && fileName === imageName) {
        file = zipFile;
      }
    });

    if (file) {
      const blob = await file.async('blob');
      const objectURL = URL.createObjectURL(blob);

      return objectURL;
    } else {
      throw new Error('Image not found in zip file.');
    }
  }

  public getImageUrlAndOrder() {
    let imageUrl = '';

    if (this.content.payload_records[0].image_id) {
      if (this.content.payload_records[0].image_id in this.values['image']) {
        imageUrl = this.values['image'][this.content.payload_records[0].image_id];
      }
    } else {
      imageUrl = this.content.payload_records[0].image_url;
    }
    if (imageUrl) {
      return {
        tag: 'img',
        value: '',
        isLoading: true,
        url: imageUrl,
        order: this.content.payload_records[0].image_display_order
      };
    } else {
      return false;
    }
  }

  public getContentText() {
    let content_text = '';
    const payloadRecord = this.content.payload_records[0];
    if (payloadRecord.content_text_id) {
      if (payloadRecord.content_text_id in this.values['string']) {
        content_text = this.values['string'][payloadRecord.content_text_id][this.language];
      }
    } else if (payloadRecord.content_text) {
      content_text = payloadRecord.content_text;
    }
    if (content_text) {
      return {
        tag: 'p',
        value: content_text,
        order: payloadRecord.context_text_display_order
      };
    } else {
      return false;
    }
  }

  public getHeadlineText() {
    let headline_text = '';
    if (this.content.headline_text_id) {
      if (this.content.headline_text_id in this.values['string']) {
        headline_text = this.values['string'][this.content.headline_text_id][this.language];
      }
    } else if (this.content.headline_text) {
      headline_text = this.content.headline_text;
    }
    if (headline_text) {
      return {
        tag: 'b',
        value: headline_text,
        order: 1
      };
    } else {
      return false;
    }
  }

  public getContentHtml() {
    let content_html = '';
    const payloadRecord = this.content.payload_records[0];
    if (payloadRecord.content_html_id) {
      if (payloadRecord.content_html_id in this.values['string']) {
        content_html = this.values['string'][payloadRecord.content_html_id][this.language];
      }
    } else if (payloadRecord.content_html) {
      content_html = payloadRecord.content_html;
    }
    if (content_html) {
      return {
        tag: 'p',
        value: content_html,
        order: payloadRecord.content_html_display_order
      };
    } else {
      return false;
    }
  }

  public getActionButton() {
    let button_text = '';
    const actionButton = this.content.action_button;
    if (actionButton && actionButton.length > 0) {
      if (actionButton[0] && actionButton[0].text_id) {
        if (actionButton[0].text_id in this.values['string']) {
          button_text = this.values['string'][actionButton[0].text_id][this.language];
        }
      } else if (actionButton[0] && actionButton[0].text) {
        button_text = actionButton[0].text;
      }
    }

    return button_text;
  }

  public getElements() {
    const elements = [];

    const image = this.getImageUrlAndOrder();
    if (image) {
      elements.push(image);
    }

    const headline = this.getHeadlineText();
    if (headline) {
      elements.push(headline);
    }

    const contentHtmlOrText = this.getContentHtml() || this.getContentText();
    if (contentHtmlOrText) {
      elements.push(contentHtmlOrText);
    }

    elements.sort((a, b) => a.order - b.order);
    return elements;
  }

  public getButtonLink() {
    const actionButton = this.content.action_button;
    if (actionButton && actionButton.length > 0) {
      if (actionButton[0] && actionButton[0].destination_uri_id) {
        if (actionButton[0].destination_uri_id in this.values['link']) {
          return this.values['link'][actionButton[0].destination_uri_id];
        }
      } else if (actionButton[0] && actionButton[0].destination_uri) {
        return actionButton[0].destination_uri;
      }
    }
  }

  public close() {
    this.ref.close();
  }
}
